import React from "react";
import { useDispatch, useSelector } from "react-redux";
import TextInput from "../components/TextInput";
import { WhatsAppIcon } from "../components/icons";
import Decimal from "decimal.js";
import Alert from "react-bootstrap/Alert";
import Modal from "react-bootstrap/Modal";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import Spinner from "react-bootstrap/Spinner";
import ListGroup from "react-bootstrap/ListGroup";
import useToast from "../hooks/useToast";
import useGoTo from "../hooks/useGoTo";
import useWorkingTime from "../hooks/useWorkingTime";
import useMPSecurity from "../hooks/useMPSecurity";
import CheckoutCosts from "./CheckoutCosts";
import CheckoutServerError from "./CheckoutServerError";
import {
  PhoneInput,
  CPFCNPJInput,
  CreditCardCVVInput,
} from "../components/NumberInput";
import { clearCart } from "../ducks/shoppingCartSlice";
import toPriceDisplay from "../utils/toPriceDisplay";
import {
  setFormAnswer,
  setIdentification,
  submitCheckout,
  selectStatefulCosts,
  selectStateSubmitValidations,
  clearCheckout,
  PIX_OPTION,
  setSubmitFinished,
} from "../ducks/checkoutSlice";
import PaymentCardImage from "../components/PaymentCardImage";
import { validateEmail, validatePhoneNumber } from "../utils/validate";
import CheckoutSection from "../components/CheckoutSection";
import domScrollTo from "../utils/domScrollTo";

export default function CheckoutForm() {
  const dispatch = useDispatch();
  const goTo = useGoTo();
  const [isFidelityVisible, setFidelityVisible] = React.useState(false);
  const [submitAttempted, setSubmitAttempted] = React.useState(false);
  const [isCVVVisible, setCVVVisible] = React.useState(false);
  const [cardCVV, setCardCVV] = React.useState("");
  const shop = useSelector((state) => state.shop);
  const costs = useSelector(selectStatefulCosts);
  const workingTime = useWorkingTime(shop);
  const submitValidation = useSelector((state) =>
    selectStateSubmitValidations(state, workingTime),
  );
  const { toastError } = useToast();
  const { isInLoco, inLocoCmd, mercadoPagoKey, isWhatsappSendingEnabled } =
    shop;
  useMPSecurity(mercadoPagoKey, "");

  const {
    email,
    name,
    telephone,
    inLocoLocation,
    payment,
    toDelivery,
    paymentOffline,
    paymentOnline,
    identification,
    isIdentificationEnable,
    identificationError,
    isSubmitting,
    cardSelected,
    cardCVV: savedCardCVV,
  } = useSelector((state) => state.checkout);

  const { fidelity } = useSelector((state) => state.shop);
  const { userId, userUuid } = useSelector((state) => state.user);
  const fidelityLogoff = fidelity && (!userId || !userUuid);
  const fidelityAboveMinimal =
    fidelity && Decimal(costs.total).lessThan(fidelity.minimalPurchase);
  const fidelityDiff = fidelityAboveMinimal
    ? Decimal(fidelity.minimalPurchase).minus(costs.total)
    : null;
  const showFidelityModal =
    !costs.fidelityDiscount && (fidelityLogoff || fidelityAboveMinimal);

  const isSubmitDisabled = submitValidation.disable;

  const onEmailChange = (event) =>
    dispatch(
      setFormAnswer({
        name: "email",
        value: event.target.value,
      }),
    );

  const onNameChange = (event) =>
    dispatch(
      setFormAnswer({
        name: "name",
        value: event.target.value,
      }),
    );

  const onIdentificationEnableChange = (event) =>
    dispatch(
      setFormAnswer({
        name: "isIdentificationEnable",
        value: event.target.checked,
      }),
    );
  const onIdentificationChange = (event) =>
    dispatch(setIdentification(event.target.value));

  const onInLocoLocationChange = (event) =>
    dispatch(
      setFormAnswer({
        name: "inLocoLocation",
        value: event.target.value,
      }),
    );

  const onTelephoneChange = (event) =>
    dispatch(
      setFormAnswer({
        name: "telephone",
        value: event.target.value,
      }),
    );

  const onCardCVVChange = (event) => setCardCVV(event.target.value);

  const emailError =
    cardSelected && !validateEmail(email) ? "Email inválido" : "";

  const nameError =
    name.length > 0 && name.length < 4
      ? "Nome com no mínimo 4 caracteres"
      : null;

  const phoneError = validatePhoneNumber(telephone) ? "" : "Celular inválido";
  // Os campos só ficam vermelhos depois da primeira tentativa de enviar: antes
  // disso, "Celular inválido" num campo que o cliente nem tocou é ruído.
  const shownError = (error) => (submitAttempted ? error : "");
  const shownNameError =
    nameError || shownError(name.trim() ? "" : "Informe seu nome");

  const cvvErrorMessage = cardSelected
    ? `Digite o código de segurança de ${cardSelected.security_code_length} dígitos que está escrito no verso do seu cartão.`
    : "";

  const cvvError =
    cardSelected && cardCVV.length !== cardSelected.security_code_length
      ? cvvErrorMessage
      : "";

  const identificationRequired =
    !isInLoco && paymentOnline && payment !== PIX_OPTION.name;

  const sendWhatsapp = !isInLoco && isWhatsappSendingEnabled && paymentOffline;

  const fieldError = () => {
    if (identificationRequired && !email.trim()) return "Informe seu email";
    if (emailError) return emailError;
    if (!name.trim()) return "Informe seu nome";
    if (nameError) return nameError;
    if (phoneError) return phoneError;
    const identificationAsked =
      identificationRequired || (!isInLoco && isIdentificationEnable);
    if (identificationAsked && !identification) return "Informe o CPF ou CNPJ";
    if (identificationError) return identificationError;
    if (isInLoco && inLocoCmd && !inLocoLocation.trim()) {
      return "Informe o local de entrega";
    }
    return null;
  };

  // O que antes era "Finalizar" → modal com os dados → "Enviar pedido" agora
  // é um toque só: os dados ficam na própria página, e este botão confere
  // tudo na mesma ordem de antes e envia.
  const handleSubmit = (event) => {
    if (event && event.preventDefault) event.preventDefault();
    if (isSubmitDisabled || isSubmitting) return;

    if (submitValidation.paymentRequired) {
      dispatch(setFormAnswer({ name: "isPaymentListVisible", value: true }));
      return;
    }
    if (toDelivery && costs.deliveryError) {
      toastError(costs.deliveryError, { autoClose: 6000 });
      return;
    }
    if (costs.couponError) {
      toastError(costs.couponError, { autoClose: 6000 });
      return;
    }
    if (costs.paymentError) {
      toastError(costs.paymentError, { autoClose: 6000 });
      return;
    }

    setSubmitAttempted(true);
    const error = fieldError();
    if (error) {
      toastError(error, { autoClose: 6000 });
      scrollToSection(CUSTOMER_SECTION_ID);
      return;
    }

    if (showFidelityModal) {
      setFidelityVisible(true);
      return;
    }

    finalizeOrder();
  };

  const finalizeOrder = () => {
    if (cardSelected && !savedCardCVV) {
      setCardCVV("");
      setCVVVisible(true);
      return;
    }
    dispatchSubmitCheckout();
  };

  const onSubmitCVV = (event) => {
    event.preventDefault();

    if (cvvError) {
      toastError(cvvError, { autoClose: 6000 });
    } else {
      dispatchSubmitCheckout(cardCVV);
    }
  };

  function dispatchSubmitCheckout(cvv) {
    const windowReference = sendWhatsapp ? window.open() : null;

    dispatch(submitCheckout({ cvv }))
      .then(({ shortReference, whatsappURI }) => {
        if (windowReference && whatsappURI) {
          windowReference.location = whatsappURI;
        }

        dispatch(clearCart());
        dispatch(clearCheckout());
        // Havia um goTo("/") aqui antes desta linha. Como o useGoTo usa
        // history.replace, ele não criava entrada de histórico nenhuma: era
        // sobrescrito na linha seguinte sem efeito nenhum.
        goTo(`/status/${shortReference}`);
      })
      .catch((err) => {
        if (windowReference) {
          windowReference.close();
        }

        if (!err.isUserReadable) {
          toastError("Erro desconhecido tentar enviar pedido.", {
            autoClose: 6000,
          });
        }

        dispatch(setSubmitFinished());
        setCVVVisible(false);
      });
  }

  const cancelFidelity = () => setFidelityVisible(false);
  const redirectToProductsPage = () => goTo("/");
  const redirectToFidelityPage = () => goTo("/fidelity");
  const continueWithoutFidelity = () => {
    setFidelityVisible(false);
    finalizeOrder();
  };

  const cancelCVV = () => setCVVVisible(false);

  const ctaLabel = isSubmitting
    ? "Enviando..."
    : isSubmitDisabled
      ? submitValidation.message
      : submitValidation.paymentRequired
        ? "Escolher pagamento"
        : sendWhatsapp
          ? "Enviar no WhatsApp"
          : submitValidation.isScheduledOrder
            ? "Agendar pedido"
            : "Fazer pedido";

  return (
    <>
      <CheckoutSection id={CUSTOMER_SECTION_ID} title="Seus dados">
        <Form className="checkout-form" onSubmit={handleSubmit} noValidate>
          {identificationRequired && (
            <TextInput
              type="email"
              label="Email"
              error={!!shownError(emailError)}
              helperText={shownError(emailError)}
              value={email}
              onChange={onEmailChange}
              inputProps={{ maxLength: 64, autoComplete: "email" }}
              required
            />
          )}
          <TextInput
            type="text"
            label="Nome"
            error={!!shownNameError}
            helperText={shownNameError}
            value={name}
            onChange={onNameChange}
            inputProps={{ maxLength: 64, autoComplete: "name" }}
            required
          />
          <TextInput
            label="Celular com DDD"
            error={!!shownError(phoneError)}
            helperText={
              shownError(phoneError) || "Para avisar sobre o seu pedido"
            }
            value={telephone}
            onChange={onTelephoneChange}
            inputComponent={PhoneInput}
            inputProps={{ autoComplete: "tel-national", type: "tel" }}
            required
          />
          {identificationRequired && (
            <TextInput
              error={!!shownError(identificationError)}
              helperText={shownError(identificationError)}
              label="CPF ou CNPJ"
              value={identification}
              onChange={onIdentificationChange}
              required
              inputComponent={CPFCNPJInput}
            />
          )}
          {!isInLoco && !identificationRequired && (
            <Form.Check
              type="switch"
              id="checkout-identification-switch"
              className="checkout-form__switch"
              checked={isIdentificationEnable}
              onChange={onIdentificationEnableChange}
              label="CPF ou CNPJ na nota?"
            />
          )}
          {!isInLoco && !identificationRequired && isIdentificationEnable && (
            <TextInput
              error={!!shownError(identificationError)}
              helperText={shownError(identificationError)}
              label="CPF ou CNPJ"
              value={identification}
              onChange={onIdentificationChange}
              inputComponent={CPFCNPJInput}
              required
            />
          )}
          {isInLoco && inLocoCmd && (
            <TextInput
              helperText="Ex: Mesa 12 ou Balcão"
              label="Local de entrega"
              value={inLocoLocation}
              onChange={onInLocoLocationChange}
              required
              inputProps={{ maxLength: 30 }}
            />
          )}
          {/* Enter no teclado do celular envia, como antes no modal */}
          <button type="submit" className="d-none" tabIndex={-1} />
        </Form>
      </CheckoutSection>

      {costs.showSummary && (
        <CheckoutSection title="Resumo">
          <CheckoutCosts />
        </CheckoutSection>
      )}

      <footer className="checkout-footer fixed-bottom">
        <div className="checkout-footer__total">
          <span className="checkout-footer__total-label">Total</span>
          <strong>{toPriceDisplay(costs.total)}</strong>
        </div>
        <button
          type="button"
          className={`btn checkout-footer__cta ${
            sendWhatsapp && !isSubmitDisabled ? "btn-whatsapp" : "btn-shop"
          }`}
          disabled={isSubmitDisabled || isSubmitting}
          onClick={handleSubmit}
        >
          {isSubmitting && (
            <Spinner animation="border" size="sm" className="me-2" />
          )}
          {sendWhatsapp && !isSubmitDisabled && !isSubmitting && (
            <WhatsAppIcon className="me-2" fontSize="small" />
          )}
          <span className="checkout-footer__label">{ctaLabel}</span>
        </button>
      </footer>

      <Modal show={isFidelityVisible} onHide={cancelFidelity}>
        <Modal.Header closeButton>
          <Modal.Title as="h3">Quer um desconto?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {fidelityLogoff && (
            <p>Faça login para participar do Programa de Fidelidade!</p>
          )}

          {fidelityAboveMinimal && (
            <p>{`Com mais ${toPriceDisplay(
              fidelityDiff,
            )} em produtos esse pedido será válido para o programa de fidelidade`}</p>
          )}

          {fidelityLogoff && (
            <Button
              variant="shop"
              className="w-100 d-flex align-items-center justify-content-between p-3 mb-3"
              onClick={redirectToFidelityPage}
            >
              Participar do fidelidade
            </Button>
          )}

          {!fidelityLogoff && fidelityAboveMinimal && (
            <Button
              variant="shop"
              className="w-100 d-flex align-items-center justify-content-between p-3 mb-3"
              onClick={redirectToProductsPage}
            >
              Adicionar mais produtos
            </Button>
          )}

          <Button
            variant="outline-shop"
            className="w-100 d-flex center justify-content-between p-3"
            onClick={continueWithoutFidelity}
          >
            Continuar sem fidelidade
          </Button>
        </Modal.Body>
      </Modal>

      <Modal show={isCVVVisible} onHide={cancelCVV}>
        <Modal.Header closeButton>
          <Modal.Title as="h3">Confirme o pagamento</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {cardSelected && (
            <ListGroup.Item className="d-flex flex-row align-items-center">
              <PaymentCardImage
                paymentLabel={cardSelected.issuer_name}
                className="me-4"
              />
              <div>
                {cardSelected.issuer_name}
                <br />
                {`**** ${cardSelected.last_four_digits}`}
              </div>
            </ListGroup.Item>
          )}

          <TextInput
            className="mt-4 mb-1"
            label="CVV"
            value={cardCVV}
            onChange={onCardCVVChange}
            inputComponent={CreditCardCVVInput}
            required
          />

          <Alert variant="info" className="text-center mt-1 mb-4">
            <small className="text-muted">{cvvErrorMessage}</small>
          </Alert>

          <Button
            variant="shop"
            className="w-100 d-flex align-items-center justify-content-between p-3"
            onClick={onSubmitCVV}
          >
            Confirmar
            {isSubmitting && (
              <Spinner animation="border" size="sm" className="me-2" />
            )}
            <span>{toPriceDisplay(costs.total)}</span>
          </Button>
        </Modal.Body>
      </Modal>

      <CheckoutServerError />
    </>
  );
}

const CUSTOMER_SECTION_ID = "checkout-seus-dados";

function scrollToSection(id) {
  const section = document.getElementById(id);
  if (!section) return;
  const top = section.getBoundingClientRect().top + window.pageYOffset - 72;
  domScrollTo(top);
}
